/**
* ログイン管理
*/

import axios from '@/plugins/axios';
import router from '@/router';
import cf from '@/mixins/commonFunctions';

// 閲覧にログインが必要のページ名（配下含め）
const requiredLoginPageNames = [
  'admin',
  'edit',
  'menu',
  'order-list',
  'order-confirm',
  'order-complete',
  'staff-called',
  'checkout-confirm',
  'checkout',
];


const actions = {
  /** state.isLoginの変更 */
  changeLoginStatus({ commit }, bool) {
    commit('changeLoginStatus', bool);
  },

  /**
   * localStorageのJWTチェックを削除し、サーバーに直接セッションの有効性を問い合わせる
   */
  check({ dispatch }) {
    dispatch('authCheck').catch((error) => {
      if (!error.response) {
        // ネットワークエラーまたはサーバーが利用できない場合の処理
        alert('ネットワークエラーが発生しました。インターネット接続を確認してください。');
        return;
      }
      // 認証に失敗した場合（未ログイン時）のページリダイレクト処理
      const currentPath = router.options.history.location;
      const isRedirect = requiredLoginPageNames.some((pageName) => {
        if (currentPath.includes(pageName)) return true;
        return false;
      });

      if (isRedirect) {
        alert('本ページを閲覧するにはログインしている必要があります。\nログインページへリダイレクトします。');
        const current = router.options.history;
        const ref = { path: current.location };
        cf.saveLocalStorage({ loginRef: JSON.stringify(ref) }, 'sma_order');
        dispatch('redirect2Login');
      }
    });
  },

  /**
   * API通信してIDとPASSをpost
   * @param obj data
   * email:      str
   * password:   str
   */
  submit({ commit, dispatch }, data) {
    axios({
      method: 'post',
      url: '/v1/auth/login',
      data,
      withCredentials: true,
    })
      .then((response) => {
        const res = response.data;
        commit('changeLoginStatus', true);
        commit('user/setUserData', res.user, { root: true });

        commit('changeBannedStatus', { isBanned: false, unbannedTime: null });

        const ls = cf.getLocalStorage('sma_order');
        if (ls && ls.loginRef) {
          const ref = JSON.parse(ls.loginRef);
          window.location.href = ref.path; // router.push を window.location.href に変更
          cf.deleteLocalStorage('sma_order', 'loginRef');
        } else {
          window.location.href = '/'; // router.push を window.location.href に変更
        }
      })
      .catch((error) => {
        const mailaddress = data.email;
        dispatch('loginFailed');
        if (error.response) {
          const errRes = error.response.data;
          console.log(errRes);
          if (errRes.detail === `E_USER_NOT_FOUND: Cannot find user with email as ${mailaddress}`) {
            commit('changeLoginErrorStatus', 1);
          } else if (errRes.detail === 'E_PASSWORD_MISMATCH: Cannot verify user password') {
            commit('changeLoginErrorStatus', 2);
          }
          if (errRes.isBanned) {
            commit('changeBannedStatus', { isBanned: errRes.isBanned, unbannedTime: errRes.unbannedTime });
          } else if (errRes.ban) {
            commit('changeBannedStatus', { isBanned: errRes.ban.isBanned, unbannedTime: errRes.ban.unbannedTime });
          } else {
            commit('changeBannedStatus', { isBanned: false, unbannedTime: null });
          }
        } else console.log(error);
      });
  },


  /**
   * auth情報を使ってログインチェックし
   * 取得したユーザーデータをstateへ格納
   * @param obj data
   * jwt?: obj
   * type: 'check' || 'login'
   * isRetry: bool
   */
  authCheck({ commit }) {
    return new Promise((resolve, reject) => {
      axios({
        method: 'POST',
        url: '/v1/auth/check',
        withCredentials: true,
      })
        .then((response) => {
          const res = response.data;
          if (res.check && res.user) {
            commit('changeLoginStatus', true);
            commit('user/setUserData', res.user, { root: true });
            resolve(response);
          } else {
            commit('changeLoginStatus', false);
            reject(new Error('User not authenticated'));
          }
        })
        .catch((error) => {
          commit('changeLoginStatus', false);
          reject(error);
        })
        .finally(() => {
          commit('changeCheckedStatus', true);
        });
    });
  },


  /** ログイン失敗 */
  loginFailed({ commit }) {
    commit('changeVibrationStatus', true);
    setTimeout(() => { commit('changeVibrationStatus', false); }, 500);
  },


  /** 有効期限切れ時のログイン画面リダイレクト */
  redirect2Login() {
    router.push({ path: '/login/' });
  },


  /** トップへリダイレクト */
  redirect2Top() {
    router.push({ path: '/' });
  },


  /** ログアウト */
  logout({ commit, dispatch }) {
    return new Promise((resolve, reject) => {
      axios({
        method: 'POST',
        url: '/v1/auth/logout',
        withCredentials: true,
      })
        .then(() => {
          // サーバーでのログアウト成功後にクライアント処理を実行
          cf.deleteLocalStorage('sma_order', 'loginRef');
          cf.deleteLocalStorage('sma_order', 'lot');

          dispatch('helper/reset', null, { root: true });
          commit('changeLoginStatus', false);

          window.location.href = '/'; // router.push を window.location.href に変更
          resolve(); // Promiseを解決
        })
        .catch((error) => {
          console.error('サーバーサイドでのログアウト処理に失敗しました:', error);
          // ユーザーにエラーを通知する (例: alertやUIコンポーネント)
          alert('ログアウトに失敗しました。サーバーとの通信に問題がある可能性があります。');
          reject(error); // Promiseを拒否
        });
    });
  },
};

const mutations = {
  /** チェック済みステート */
  changeCheckedStatus(state, bool) {
    state.isChecked = bool;
  },

  /** ログインステート */
  changeLoginStatus(state, bool) {
    state.isLogin = bool;
  },

  /** ログイン失敗時のバイブフラグステート */
  changeVibrationStatus(state, bool) {
    state.vibration = bool;
  },

  /** ログイン制限時のフラグ・日時ステート */
  changeBannedStatus(state, { isBanned: bool, unbannedTime: datetime }) {
    state.isBanned = bool;
    state.unbannedTime = datetime;
  },

  /** ログイン失敗時のエラー内容ステート */
  changeLoginErrorStatus(state, number) {
    if (number === 0) {
      state.isFailed = 0;
    } else {
      state.isFailed = number;
    }
  },
};

const state = {
  isChecked: false,
  isLogin: false,
  vibration: false,
  isBanned: false,
  unbannedTime: null,
  isFailed: 0,
};


export default {
  namespaced: true,
  actions,
  mutations,
  state,
};
